<script setup>
import { ref, onMounted, watch } from 'vue'
import { useField } from 'vee-validate'
import { useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import { useFormValidation } from '@/composables/useFormValidation'
import { resetPasswordSchema } from '@/formValidationschemas'
import InputError from '@/components/general/InputError.vue'
import InputLabel from '@/components/general/InputLabel.vue'
import PrimaryButton from '@/components/general/PrimaryButton.vue'
import TextInput from '@/components/general/TextInput.vue'
import { useRoute } from 'vue-router'
import { ROUTES } from '@/router/routeNames'
import { useAuthStore } from '@/stores/authStore'

const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()
const toast = useToast()
const processing = ref(false)
const serverErrors = ref({})

// Initialize form with vee-validate
const initialValues = {
  token: route.query.token,
  email: route.query.email,
  password: '',
  confirmPassword: '',
}

const { handleSubmit, errors, resetForm } = useFormValidation(resetPasswordSchema, initialValues)

onMounted(async () => {
  if (!route.query.token || !route.query.email) {
    router.push({ name: ROUTES.LOGIN })
  }
  if (route.query.email) {
    email.value = route.query.email
  }
})

const { error, mutateAsync } = authStore.resetPassword()

// Setup form fields with useField
const { value: email } = useField('email')
const { value: password } = useField('password')
const { value: confirmPassword } = useField('confirmPassword')

const submit = handleSubmit(async (values) => {
  processing.value = true
  serverErrors.value = {}

  try {
    await mutateAsync({
      token: route.query.token,
      email: values.email,
      password: values.password,
      confirmPassword: values.confirmPassword,
    })

    // Reset form after successful submission
    resetForm()

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Your password has been reset successfully',
      life: 5000,
    })

    // Redirect to login page
    router.push({ name: ROUTES.LOGIN })
  } catch (error) {
    if (error.response && error.response.data && error.response.data.errors) {
      // Store backend validation errors
      serverErrors.value = error.response.data.errors
    } else {
      // Generic error handling
      console.error('Failed to reset password:', error)
    }
  } finally {
    processing.value = false
  }
})
watch(
  () => error.value,
  (newValue) => {
    const message = newValue?.response.data.message
    if (message) {
      toast.add({
        severity: 'error',
        summary: 'Sorry we could not reset your password',
        detail: message,
        life: 3000,
      })
    }
  },
)
</script>

<template>
  <div class="flex flex-col justify-center items-center px-4">
    <!-- Card Container -->
    <div class="p-8 w-full max-w-md bg-white rounded-2xl shadow-lg">
      <h2 class="mb-6 text-2xl font-bold text-gray-900">Reset Password</h2>

      <div class="mb-6 text-sm text-gray-600">Please set your new password below.</div>

      <form @submit.prevent="submit">
        <div class="mb-6">
          <InputLabel for="email" value="Email" />
          <TextInput
            id="email"
            v-model="email"
            type="email"
            class="block px-4 py-3 mt-1 w-full text-gray-900 rounded-xl border-0 ring-1 ring-inset ring-gray-300 shadow-sm placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            required
            readonly
            autocomplete="username"
          />
          <InputError
            :message="errors.email || (serverErrors.email ? serverErrors.email[0] : '')"
            class="mt-2"
          />
        </div>

        <div class="mb-6">
          <InputLabel for="password" value="New Password" />
          <TextInput
            id="password"
            v-model="password"
            type="password"
            class="block px-4 py-3 mt-1 w-full text-gray-900 rounded-xl border-0 ring-1 ring-inset ring-gray-300 shadow-sm placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            required
            autofocus
            autocomplete="new-password"
          />
          <InputError
            :message="errors.password || (serverErrors.password ? serverErrors.password[0] : '')"
            class="mt-2"
          />
        </div>

        <div class="mb-6">
          <InputLabel for="confirmPassword" value="Confirm Password" />
          <TextInput
            id="confirmPassword"
            v-model="confirmPassword"
            type="password"
            class="block px-4 py-3 mt-1 w-full text-gray-900 rounded-xl border-0 ring-1 ring-inset ring-gray-300 shadow-sm placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            required
            autocomplete="new-password"
          />
          <InputError
            :message="
              errors.confirmPassword ||
              (serverErrors.password_confirmation ? serverErrors.password_confirmation[0] : '')
            "
            class="mt-2"
          />
        </div>

        <div class="flex justify-end items-center mt-6">
          <PrimaryButton type="submit" :disabled="processing"> Reset Password </PrimaryButton>
        </div>
      </form>

      <!-- Back to Login Link -->
      <div class="mt-6 text-center">
        <RouterLink
          :to="{ name: 'login' }"
          class="text-sm font-medium text-green-600 hover:text-green-500"
        >
          Back to Login
        </RouterLink>
      </div>
    </div>
  </div>
</template>
